import { BookIcon, GiftIcon, PawPrintIcon, StarIcon } from 'lucide-preact';
import { useCallback } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import AppStoreDownloadButton from '@/components/AppStoreDownloadButton';
import BurgerMenu from '@/components/BurgerMenu';
import { LandingSecondary } from '@/components/Buttons';
import NavItem from '@/components/NavBar/NavItem';
import { useShouldDisableJSRouting } from '@/components/providers/DisableJSRoutingProvider';
import SocialNetworkList from '@/components/SocialNetworkList';
import useOpenSpeedyFlow from '@/components/SpeedyFlow/hooks/useOpenSpeedyFlow';
import speedyFlowSlice from '@/components/SpeedyFlow/redux/slice';
import { getHomeURL, isEmbedded } from '@/helpers';
import { useSelector } from '@/redux/helper';
import { goToSignup } from '@/routes/home/helpers';
import { logLandingButton } from '@/shared/ga';

import TalkNavItem from '../TalkNavItem';

const { advanceSpeedyFlow } = speedyFlowSlice.actions;

const PublicHeaderContent = ({ isLargeScreen, logEvents = false }: { isLargeScreen?: boolean; logEvents?: boolean }) => {
  const dispatch = useDispatch();
  const openSpeedyFlow = useOpenSpeedyFlow();
  const giftCardConfig = useSelector((state) => state.remoteConfig.giftCardConfig);
  const shouldDisableJSRouting = useShouldDisableJSRouting();
  const embedded = isEmbedded();

  const origin = window?.location.origin ?? '';
  const target = shouldDisableJSRouting ? '_parent' : embedded ? '_blank' : undefined;
  const rootDomainURL = getHomeURL();

  const logEvent = useCallback(
    (action: string) => {
      if (logEvents) {
        logLandingButton(action);
      }
    },
    [logEvents],
  );

  /**
   * Whether the flow can run here at all.
   *
   * Not in an iframe: a modal inside someone else's page is the wrong shape, and the header already
   * targets `_parent`/`_blank` there for exactly that reason. Not when JS routing is off either —
   * that mode exists to make every link a real navigation.
   */
  const canOpenInPlace = !embedded && !shouldDisableJSRouting;

  /**
   * Sign up without leaving the page.
   *
   * The header was the last obvious way in still doing the old thing: a visitor who decides to join
   * while looking at something lost whatever they were looking at. The flow's opening step offers
   * both directions, so one gate serves this and the log-in link beside it.
   *
   * Falls through to the full page whenever the flow declines to open — which for a signed-out
   * visitor it should not, but a gate that resolves to nothing must not swallow the click.
   */
  const onPressSignUp = useCallback(() => {
    logLandingButton('Header-Sign-Up');
    if (canOpenInPlace && openSpeedyFlow({ kind: 'account', entryPoint: 'header-signup' })) {
      return;
    }
    goToSignup();
  }, [canOpenInPlace, openSpeedyFlow]);

  /**
   * The same, for someone who says they already have an account.
   *
   * The anchor is left in place and only the plain click is intercepted, so middle-click, "open in
   * new tab" and a JS-less visitor all still reach `/login`.
   *
   * The gate opens on the persona question, which is not the first thing to show someone who has
   * just told us they have an account — so it is moved on by the same edge that step's own "I
   * already have an account" button uses.
   */
  const onPressLogIn = useCallback(
    (event: MouseEvent) => {
      logEvent('Header-Log-In');
      if (!canOpenInPlace || !openSpeedyFlow({ kind: 'account', entryPoint: 'header-login' })) {
        return;
      }
      event.preventDefault();
      dispatch(advanceSpeedyFlow({ type: 'choseLogIn' }));
    },
    [canOpenInPlace, openSpeedyFlow, dispatch, logEvent],
  );

  return isLargeScreen ? (
    <>
      <NavItem
        avoidInnerPadding
        text="About"
        url={`${rootDomainURL}/about-us`}
        customIcon={<PawPrintIcon className="size-5" />}
        target={target}
        onClick={() => logEvent('Header-About')}
      />
      <NavItem
        avoidInnerPadding
        text="Features"
        url={`${rootDomainURL}/features`}
        customIcon={<StarIcon className="size-5" />}
        target={target}
        onClick={() => logEvent('Header-Features')}
      />
      <NavItem
        avoidInnerPadding
        text="For Educators"
        url={`${rootDomainURL}/educators`}
        customIcon={<BookIcon className="size-5" />}
        target={target}
        onClick={() => logEvent('Header-For-Educators')}
      />
      <TalkNavItem avoidInnerPadding target={target} onClick={() => logEvent('Header-Schedules')} />
      <div className="ml-5 flex items-center justify-center gap-3">
        <NavItem avoidInnerPadding text="Log in" url={`${origin}/login`} target={target} onClick={onPressLogIn} />
        <LandingSecondary onClick={onPressSignUp} className="!mr-4 !h-[34px] !w-[102px] !p-0 !text-base !font-bold">
          Sign up
        </LandingSecondary>
      </div>
    </>
  ) : (
    <BurgerMenu>
      <NavItem
        text="About"
        url={`${rootDomainURL}/about-us`}
        customIcon={<PawPrintIcon className="size-5" />}
        target={target}
        onClick={() => logEvent('Header-About')}
        className="text-base"
      />
      <NavItem
        text="Features"
        url={`${rootDomainURL}/features`}
        customIcon={<StarIcon className="size-5" />}
        target={target}
        onClick={() => logEvent('Header-Features')}
        className="text-base"
      />
      <TalkNavItem className="text-base" target={target} onClick={() => logEvent('Header-Schedules')} />
      <NavItem
        text="Gift Cards"
        url={`${origin}/gift`}
        onClick={() => logEvent('Header-Gift-Cards')}
        target={target}
        className="text-base"
        customIcon={
          giftCardConfig?.enabled && giftCardConfig?.iconURL ? (
            <img className="preflight preflight-img -mx-0.5 -mt-0.5 size-6" src={giftCardConfig.iconURL} alt="gift box" />
          ) : (
            <GiftIcon className="size-5" />
          )
        }
      />
      <div className="mt-6 flex flex-col items-center gap-6 px-4">
        <AppStoreDownloadButton className="w-full max-w-40" />
        <SocialNetworkList />
        <LandingSecondary onClick={onPressSignUp} className="!h-[52px] !w-full !p-0 !text-base !font-medium">
          Sign up
        </LandingSecondary>
        <a
          href={`${origin}/login`}
          onClick={onPressLogIn}
          className="preflight preflight-a text-base font-bold text-grey-3 group-hover:text-grey-1"
        >
          Log in
        </a>
      </div>
    </BurgerMenu>
  );
};

export default PublicHeaderContent;
